(() => {
  'use strict';

  if (window.__lcDiscoveryLoaded) return;
  window.__lcDiscoveryLoaded = true;

  const ROOT_SELECTOR = '[data-lc-discovery]';
  const STATE_KEY = 'lc-discovery-return-state-v1';
  const STATE_MAX_AGE = 30 * 60 * 1000;
  const controllers = new Set();

  const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
  const asNumber = (value, fallback = 0) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const cleanUrl = (source) => {
    const url = new URL(source, window.location.origin);
    url.searchParams.delete('section_id');
    return `${url.pathname}${url.search}${url.hash}`;
  };

  const requestRoot = async (source, sectionId, signal) => {
    const requestUrl = new URL(source, window.location.origin);
    requestUrl.searchParams.set('section_id', sectionId);
    const response = await fetch(requestUrl.toString(), {
      signal,
      headers: { Accept: 'text/html' }
    });
    if (!response.ok) throw new Error(`Discovery request failed: ${response.status}`);

    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const renderedRoot = Array.from(parsed.querySelectorAll(ROOT_SELECTOR)).find(
      (candidate) => candidate.dataset.lcSectionId === sectionId
    );
    if (!renderedRoot) throw new Error('The rendered discovery section was not found.');
    return renderedRoot;
  };

  class LcDiscoveryController {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.lcSectionId || '';
      this.kind = root.dataset.lcDiscoveryKind || 'collection';
      this.autoBatchLimit = Math.max(0, Number.parseInt(root.dataset.lcAutoBatchLimit || '0', 10) || 0);
      this.autoBatchesLoaded = 0;
      this.loadedPageUrls = [];
      this.requestController = null;
      this.loadingNext = false;
      this.restoring = false;
      this.observer = null;

      if (!this.sectionId) return;

      this.root.dataset.lcDiscoveryReady = 'true';
      this.root.addEventListener('submit', (event) => this.onSubmit(event));
      this.root.addEventListener('click', (event) => this.onClick(event));
      this.root.addEventListener('input', (event) => this.onPriceInput(event));
      this.root.addEventListener('change', (event) => this.onChange(event));
      this.root.addEventListener('keydown', (event) => this.onKeydown(event));

      this.initializePriceRanges();
      this.enhancePagination();
      this.restoreReturnState();
    }

    get results() {
      return this.root.querySelector('[data-lc-product-results]');
    }

    get grid() {
      return this.root.querySelector('[data-lc-product-grid]');
    }

    get pagination() {
      return this.root.querySelector('[data-lc-pagination]');
    }

    get statusRegion() {
      return this.root.querySelector('[data-lc-discovery-status]');
    }

    announce(message) {
      const region = this.statusRegion;
      if (!region || !message) return;
      region.textContent = '';
      window.requestAnimationFrame(() => {
        region.textContent = message;
      });
    }

    setBusy(isBusy) {
      const results = this.results;
      if (!results) return;
      if (isBusy) results.setAttribute('aria-busy', 'true');
      else results.removeAttribute('aria-busy');
    }

    formUrl(form) {
      const url = new URL(form.action, window.location.origin);
      url.search = '';
      const formData = new FormData(form);
      formData.forEach((value, name) => {
        const normalized = String(value).trim();
        if (normalized !== '') url.searchParams.append(name, normalized);
      });
      url.searchParams.delete('page');
      return url;
    }

    async closeFilterDrawer(form) {
      const drawer = form.closest('[data-lc-filter-drawer]');
      if (!drawer?.classList.contains('is-open')) return;
      drawer.querySelector('[data-drawer-close]')?.click();
      // The shared drawer utility removes its open state in 250 ms.
      await wait(270);
    }

    onSubmit(event) {
      const form = event.target.closest('[data-lc-facet-form], [data-lc-sort-form]');
      if (!form || !this.root.contains(form)) return;

      event.preventDefault();
      event.stopPropagation();
      const targetUrl = this.formUrl(form);
      const focusTarget = form.matches('[data-lc-facet-form]') ? 'filter' : 'sort';
      this.applyUrl(targetUrl, { push: true, focusTarget, closeForm: form });
    }

    applyFacetChange(form, control) {
      const facetContent = form.closest('[data-lc-facet-content]');
      const groups = form.querySelector('.lc-facets__groups');
      this.applyUrl(this.formUrl(form), {
        push: true,
        preserveViewport: true,
        facetContentKey: facetContent?.dataset.lcFacetContent || '',
        facetScrollTop: groups?.scrollTop || 0,
        restoreControlId: control.id || ''
      });
    }

    onChange(event) {
      this.onPriceChange(event);

      const control = event.target;
      const form = control.closest?.('[data-lc-facet-form]');
      if (!form || !this.root.contains(form)) return;
      if (
        !control.matches(
          'input[type="checkbox"]:not(:disabled), input[type="radio"]:not(:disabled), [data-lc-price-handle], [data-lc-price-field]'
        )
      ) {
        return;
      }

      this.applyFacetChange(form, control);
    }

    onKeydown(event) {
      if (event.key !== 'Enter') return;
      const field = event.target.closest?.('[data-lc-price-field]');
      const form = field?.closest('[data-lc-facet-form]');
      if (!field || !form || !this.root.contains(form)) return;

      event.preventDefault();
      event.stopPropagation();
      this.syncPriceFromFields(field.closest('[data-lc-price-range]'), field, true);
      this.applyFacetChange(form, field);
    }

    onClick(event) {
      const loadMore = event.target.closest('[data-lc-load-more]');
      if (loadMore && this.root.contains(loadMore)) {
        event.preventDefault();
        this.loadNextPage({ automatic: false });
        return;
      }

      const retry = event.target.closest('[data-lc-load-retry]');
      if (retry && this.root.contains(retry)) {
        event.preventDefault();
        this.loadNextPage({ automatic: false });
        return;
      }

      const discoveryLink = event.target.closest(
        '.lc-filter-chip, .lc-active-filters__clear, [data-lc-discovery-link]'
      );
      if (discoveryLink && this.root.contains(discoveryLink)) {
        const targetUrl = new URL(discoveryLink.href, window.location.origin);
        if (targetUrl.origin === window.location.origin) {
          event.preventDefault();
          this.applyUrl(targetUrl, { push: true, focusTarget: 'filter' });
        }
        return;
      }

      const productLink = event.target.closest('[data-product-card] a[href]');
      if (productLink && this.root.contains(productLink)) this.saveReturnState();
    }

    async applyUrl(target, options = {}) {
      const targetUrl = new URL(target, window.location.origin);
      targetUrl.searchParams.delete('page');
      targetUrl.searchParams.delete('section_id');
      const nativeUrl = cleanUrl(targetUrl);
      const targetScrollY = options.preserveViewport ? window.scrollY : 0;
      const targetScrollX = options.preserveViewport ? window.scrollX : 0;

      if (options.closeForm) await this.closeFilterDrawer(options.closeForm);

      this.requestController?.abort();
      this.requestController = new AbortController();
      this.disconnectObserver();
      this.setBusy(true);

      try {
        const renderedRoot = await requestRoot(targetUrl, this.sectionId, this.requestController.signal);
        this.replaceDiscoveryState(renderedRoot);
        this.loadedPageUrls = [];
        this.autoBatchesLoaded = 0;

        if (options.push) {
          window.history.pushState({ ...(window.history.state || {}), lcDiscovery: true }, '', nativeUrl);
        }

        this.initializePriceRanges();
        this.enhancePagination();
        this.setBusy(false);

        if (options.preserveViewport) {
          const matchingFacetContent = Array.from(
            this.root.querySelectorAll('[data-lc-facet-content]')
          ).find((content) => content.dataset.lcFacetContent === options.facetContentKey);
          const groups = matchingFacetContent?.querySelector('.lc-facets__groups');
          if (groups) groups.scrollTop = options.facetScrollTop || 0;

          window.scrollTo({ top: targetScrollY, left: targetScrollX, behavior: 'auto' });
          const control = options.restoreControlId
            ? document.getElementById(options.restoreControlId)
            : null;
          if (control && this.root.contains(control)) control.focus({ preventScroll: true });
        } else if (options.focusTarget === 'filter') {
          this.root.querySelector('[data-lc-filter-trigger]')?.focus({ preventScroll: true });
        } else if (options.focusTarget === 'sort') {
          this.root.querySelector('[data-lc-sort-form] [name="sort_by"]')?.focus({ preventScroll: true });
        }

        if (!options.preserveViewport) {
          const toolbar = this.root.querySelector('[data-lc-discovery-toolbar]');
          toolbar?.scrollIntoView({
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
            block: 'start'
          });
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.setBusy(false);
        // Native navigation remains the reliable fallback if section rendering fails.
        window.location.assign(nativeUrl);
      }
    }

    replaceDiscoveryState(renderedRoot) {
      const replaceOne = (selector) => {
        const current = this.root.querySelector(selector);
        const replacement = renderedRoot.querySelector(selector);
        if (current && replacement) current.replaceWith(replacement);
        else if (current && !replacement) current.remove();
      };

      replaceOne('[data-lc-discovery-toolbar]');
      replaceOne('[data-lc-active-filters]');
      replaceOne('[data-lc-product-results]');

      const currentFacetContents = Array.from(this.root.querySelectorAll('[data-lc-facet-content]'));
      const replacementFacetContents = Array.from(
        renderedRoot.querySelectorAll('[data-lc-facet-content]')
      );
      currentFacetContents.forEach((current, index) => {
        const key = current.dataset.lcFacetContent || '';
        const replacement = key
          ? replacementFacetContents.find((candidate) => candidate.dataset.lcFacetContent === key)
          : replacementFacetContents[index];
        current.innerHTML = replacement?.innerHTML || '';
      });

      const openFilterDrawer = this.root.querySelector('[data-lc-filter-drawer].is-open');
      if (openFilterDrawer) {
        Array.from(this.root.querySelectorAll('[data-drawer-open]'))
          .find((trigger) => trigger.dataset.drawerOpen === openFilterDrawer.id)
          ?.setAttribute('aria-expanded', 'true');
      }

      ['lcTotalCount', 'lcPageSize', 'lcAutoBatchLimit'].forEach((key) => {
        if (renderedRoot.dataset[key] !== undefined) this.root.dataset[key] = renderedRoot.dataset[key];
      });
      this.autoBatchLimit = Math.max(
        0,
        Number.parseInt(this.root.dataset.lcAutoBatchLimit || '0', 10) || 0
      );
    }

    disconnectObserver() {
      this.observer?.disconnect();
      this.observer = null;
    }

    enhancePagination() {
      this.disconnectObserver();
      const pagination = this.pagination;
      if (!pagination?.querySelector('[data-lc-progressive-pagination]')) return;

      pagination.querySelector('[data-lc-numbered-pagination]')?.setAttribute('hidden', '');
      this.configureNextPageControl();
      this.updateProgress();
    }

    configureNextPageControl() {
      const pagination = this.pagination;
      const loadMore = pagination?.querySelector('[data-lc-load-more]');
      const sentinel = pagination?.querySelector('[data-lc-load-sentinel]');
      if (!loadMore || !sentinel) return;

      if (
        !this.restoring &&
        this.autoBatchesLoaded < this.autoBatchLimit &&
        'IntersectionObserver' in window
      ) {
        loadMore.hidden = true;
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.disconnectObserver();
            this.loadNextPage({ automatic: true });
          },
          { rootMargin: '600px 0px' }
        );
        this.observer.observe(sentinel);
      } else {
        loadMore.hidden = false;
      }
    }

    async loadNextPage(options = {}) {
      if (this.loadingNext) return false;
      const loadMore = this.pagination?.querySelector('[data-lc-load-more]');
      const requestedUrl = options.url || loadMore?.href;
      if (!requestedUrl) return false;

      this.loadingNext = true;
      this.requestController?.abort();
      this.requestController = new AbortController();
      this.disconnectObserver();
      this.setBusy(true);
      loadMore?.setAttribute('aria-busy', 'true');
      const retry = this.pagination?.querySelector('[data-lc-load-retry]');
      if (retry) retry.hidden = true;

      try {
        const renderedRoot = await requestRoot(requestedUrl, this.sectionId, this.requestController.signal);
        const renderedGrid = renderedRoot.querySelector('[data-lc-product-grid]');
        const currentGrid = this.grid;
        if (!renderedGrid || !currentGrid) throw new Error('The next product grid was not found.');

        const existingHandles = new Set(
          Array.from(currentGrid.querySelectorAll('[data-product-card]'))
            .map((card) => card.dataset.productHandle)
            .filter(Boolean)
        );
        const fragment = document.createDocumentFragment();
        let addedCount = 0;

        renderedGrid.querySelectorAll('[data-product-card]').forEach((card) => {
          const handle = card.dataset.productHandle;
          if (handle && existingHandles.has(handle)) return;
          if (handle) existingHandles.add(handle);
          fragment.appendChild(card);
          addedCount += 1;
        });
        currentGrid.appendChild(fragment);

        const requestedNativeUrl = cleanUrl(requestedUrl);
        if (!this.loadedPageUrls.includes(requestedNativeUrl)) this.loadedPageUrls.push(requestedNativeUrl);
        if (options.automatic) this.autoBatchesLoaded += 1;

        const renderedPagination = renderedRoot.querySelector('[data-lc-pagination]');
        const renderedNext = renderedPagination?.querySelector('[data-lc-load-more]');
        const currentPagination = this.pagination;
        const currentNext = currentPagination?.querySelector('[data-lc-load-more]');
        const currentSentinel = currentPagination?.querySelector('[data-lc-load-sentinel]');

        if (renderedNext && currentNext) {
          currentNext.href = renderedNext.href;
          currentNext.hidden = false;
          currentNext.removeAttribute('aria-busy');
        } else {
          currentNext?.remove();
          currentSentinel?.remove();
        }

        this.updateProgress();
        if (!options.restoring) {
          const label = currentPagination?.dataset.lcItemLabel || 'products';
          this.announce(`${addedCount} more ${label} loaded. ${this.progressText()}`);
        }
        this.setBusy(false);
        this.loadingNext = false;
        this.saveReturnState();
        this.configureNextPageControl();
        return true;
      } catch (error) {
        this.setBusy(false);
        this.loadingNext = false;
        loadMore?.removeAttribute('aria-busy');
        if (error.name === 'AbortError') return false;

        const currentRetry = this.pagination?.querySelector('[data-lc-load-retry]');
        const currentLoadMore = this.pagination?.querySelector('[data-lc-load-more]');
        if (currentRetry) currentRetry.hidden = false;
        if (currentLoadMore) currentLoadMore.hidden = false;
        this.announce('More products could not be loaded. Retry or follow the Load more link.');
        return false;
      }
    }

    progressText() {
      const pagination = this.pagination;
      if (!pagination) return '';
      const shown = this.grid?.querySelectorAll('[data-product-card]').length || 0;
      const total = asNumber(this.root.dataset.lcTotalCount, asNumber(pagination.dataset.lcTotalCount, shown));
      const label = pagination.dataset.lcItemLabel || 'products';
      return `${Math.min(shown, total)} of ${total} ${label} shown`;
    }

    updateProgress() {
      const pagination = this.pagination;
      const progress = pagination?.querySelector('[data-lc-progress]');
      if (!pagination || !progress) return;
      const shown = this.grid?.querySelectorAll('[data-product-card]').length || 0;
      pagination.dataset.lcShownCount = String(shown);
      progress.textContent = this.progressText();
    }

    formatCurrency(value, range) {
      const currency = range.dataset.lcCurrency || window.LightingCentre?.currency || 'CAD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
          style: 'currency',
          currency,
          maximumFractionDigits: 2
        }).format(value);
      } catch (_error) {
        return `${value.toFixed(2)} ${currency}`;
      }
    }

    priceParts(range) {
      return {
        minimumHandle: range.querySelector('[data-lc-price-handle="min"]'),
        maximumHandle: range.querySelector('[data-lc-price-handle="max"]'),
        minimumField: range.querySelector('[data-lc-price-field="min"]'),
        maximumField: range.querySelector('[data-lc-price-field="max"]'),
        status: range.querySelector('[data-lc-price-status]'),
        rangeMaximum: asNumber(range.dataset.lcRangeMax, 0)
      };
    }

    initializePriceRanges() {
      this.root.querySelectorAll('[data-lc-price-range]').forEach((range) => {
        this.syncPriceFromFields(range, null, false);
      });
    }

    syncPriceFromHandles(range, source, announceChange) {
      const parts = this.priceParts(range);
      if (!parts.minimumHandle || !parts.maximumHandle) return;

      let minimum = clamp(asNumber(parts.minimumHandle.value, 0), 0, parts.rangeMaximum);
      let maximum = clamp(
        asNumber(parts.maximumHandle.value, parts.rangeMaximum),
        0,
        parts.rangeMaximum
      );
      if (minimum > maximum) {
        if (source === parts.minimumHandle) minimum = maximum;
        else maximum = minimum;
      }

      parts.minimumHandle.value = String(minimum);
      parts.maximumHandle.value = String(maximum);
      parts.minimumHandle.setAttribute('aria-valuetext', this.formatCurrency(minimum, range));
      parts.maximumHandle.setAttribute('aria-valuetext', this.formatCurrency(maximum, range));
      if (parts.minimumField) parts.minimumField.value = String(minimum);
      if (parts.maximumField) parts.maximumField.value = String(maximum);

      if (announceChange && parts.status) {
        parts.status.textContent = `${this.formatCurrency(minimum, range)} to ${this.formatCurrency(maximum, range)}`;
      }
    }

    syncPriceFromFields(range, source, announceChange) {
      const parts = this.priceParts(range);
      if (!parts.minimumHandle || !parts.maximumHandle) return;

      const minimumBlank = !parts.minimumField?.value.trim();
      const maximumBlank = !parts.maximumField?.value.trim();
      let minimum = minimumBlank ? 0 : asNumber(parts.minimumField.value, 0);
      let maximum = maximumBlank
        ? parts.rangeMaximum
        : asNumber(parts.maximumField.value, parts.rangeMaximum);
      minimum = clamp(minimum, 0, parts.rangeMaximum);
      maximum = clamp(maximum, 0, parts.rangeMaximum);

      if (minimum > maximum) {
        if (source === parts.minimumField) minimum = maximum;
        else maximum = minimum;
      }

      parts.minimumHandle.value = String(minimum);
      parts.maximumHandle.value = String(maximum);
      parts.minimumHandle.setAttribute('aria-valuetext', this.formatCurrency(minimum, range));
      parts.maximumHandle.setAttribute('aria-valuetext', this.formatCurrency(maximum, range));

      if (announceChange) {
        if (parts.minimumField && !minimumBlank) parts.minimumField.value = String(minimum);
        if (parts.maximumField && !maximumBlank) parts.maximumField.value = String(maximum);
        if (parts.status) {
          parts.status.textContent = `${this.formatCurrency(minimum, range)} to ${this.formatCurrency(maximum, range)}`;
        }
      }
    }

    onPriceInput(event) {
      const handle = event.target.closest('[data-lc-price-handle]');
      if (handle && this.root.contains(handle)) {
        this.syncPriceFromHandles(handle.closest('[data-lc-price-range]'), handle, false);
        return;
      }

      const field = event.target.closest('[data-lc-price-field]');
      if (field && this.root.contains(field)) {
        this.syncPriceFromFields(field.closest('[data-lc-price-range]'), field, false);
      }
    }

    onPriceChange(event) {
      const handle = event.target.closest('[data-lc-price-handle]');
      if (handle && this.root.contains(handle)) {
        this.syncPriceFromHandles(handle.closest('[data-lc-price-range]'), handle, true);
        return;
      }

      const field = event.target.closest('[data-lc-price-field]');
      if (field && this.root.contains(field)) {
        this.syncPriceFromFields(field.closest('[data-lc-price-range]'), field, true);
      }
    }

    saveReturnState() {
      if (this.kind !== 'collection') return;
      try {
        window.sessionStorage.setItem(
          STATE_KEY,
          JSON.stringify({
            version: 1,
            url: cleanUrl(window.location.href),
            scrollY: window.scrollY,
            loadedPageUrls: this.loadedPageUrls,
            timestamp: Date.now()
          })
        );
      } catch (_error) {
        // Session restoration is progressive enhancement only.
      }
    }

    async restoreReturnState() {
      if (this.kind !== 'collection' || !this.pagination) return;

      let state;
      try {
        state = JSON.parse(window.sessionStorage.getItem(STATE_KEY) || 'null');
      } catch (_error) {
        return;
      }
      if (
        !state ||
        state.version !== 1 ||
        state.url !== cleanUrl(window.location.href) ||
        Date.now() - Number(state.timestamp || 0) > STATE_MAX_AGE
      ) {
        return;
      }

      const navigationType = window.performance.getEntriesByType?.('navigation')?.[0]?.type;
      let returningFromProduct = false;
      try {
        returningFromProduct = new URL(document.referrer).pathname.includes('/products/');
      } catch (_error) {
        returningFromProduct = false;
      }
      if (navigationType !== 'back_forward' && !returningFromProduct) return;

      const savedUrls = Array.isArray(state.loadedPageUrls) ? state.loadedPageUrls : [];
      const restoredUrls = [];
      this.restoring = true;
      this.disconnectObserver();
      for (const pageUrl of savedUrls) {
        const restored = await this.loadNextPage({ url: pageUrl, restoring: true, automatic: false });
        if (!restored) break;
        restoredUrls.push(pageUrl);
      }
      this.loadedPageUrls = restoredUrls;
      this.autoBatchesLoaded = Math.min(restoredUrls.length, this.autoBatchLimit);
      this.restoring = false;
      this.configureNextPageControl();
      window.requestAnimationFrame(() => window.scrollTo({ top: Number(state.scrollY || 0), behavior: 'auto' }));
    }

    onPopState() {
      if (!this.root.isConnected) return;
      this.applyUrl(window.location.href, { push: false });
    }
  }

  const initialize = (scope = document) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => {
      if (root.dataset.lcDiscoveryInitialized === 'true') return;
      root.dataset.lcDiscoveryInitialized = 'true';
      const controller = new LcDiscoveryController(root);
      controllers.add(controller);
    });
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  window.addEventListener('popstate', () => {
    controllers.forEach((controller) => {
      if (controller.root.isConnected) controller.onPopState();
      else controllers.delete(controller);
    });
  });
  window.addEventListener('pagehide', () => {
    controllers.forEach((controller) => {
      if (controller.root.isConnected) controller.saveReturnState();
    });
  });
})();
